import {omneticApi} from '@dms/api/base';
import {UuidResponseBody, UuidResponseBodySchema} from '@dms/api/shared';

import {
  ChangeWorkshopInterestBranchApiArg,
  ChangeWorkshopInterestBranchRequestBodySchema,
  ChangeWorkshopInterestContactPersonApiArg,
  ChangeWorkshopInterestContactPersonRequestBodySchema,
  CreateWorkshopInterestActivityApiArg,
  CreateWorkshopInterestActivityApiResponse,
  CreateWorkshopInterestActivityRequestBodySchema,
  CreateWorkshopInterestApiArg,
  CreateWorkshopInterestRequestBodySchema,
  DeleteWorkshopInterestApiArg,
  EndWorkshopInterestApiArg,
  EndWorkshopInterestRequestBodySchema,
  GetWorkshopInterestApiArg,
  GetWorkshopInterestApiArgSchema,
  GetWorkshopInterestApiResponse,
  ListWorkshopInterestActivitiesApiArg,
  ListWorkshopInterestActivitiesApiArgSchema,
  ListWorkshopInterestActivitiesApiResponse,
  OpenWorkshopInterestApiArg,
  RemoveVehicleApiArg,
  SelectVehicleApiArg,
  SelectVehicleRequestBodySchema,
  SetWorkshopInterestSourceApiArg,
  SetWorkshopInterestSourceRequestBodySchema,
  WorkshopInterestActivityResponseBodySchema,
  WorkshopInterestResponseBodySchema,
} from './types';

const injected = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    createWorkshopInterest: build.mutation<UuidResponseBody, CreateWorkshopInterestApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest`,
        method: 'POST',
        body: queryArg.createWorkshopInterestRequestBody,
        queryArg,
      }),
      invalidatesTags: ['WorkshopInterest'],
      extraOptions: {
        requestSchema: CreateWorkshopInterestRequestBodySchema,
        responseSchema: UuidResponseBodySchema,
      },
    }),

    getWorkshopInterest: build.query<GetWorkshopInterestApiResponse, GetWorkshopInterestApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest/${queryArg.id}`,
        queryArg,
      }),
      providesTags: (result, error, queryArg) => [{type: 'WorkshopInterest', id: queryArg.id}],
      extraOptions: {
        requestSchema: GetWorkshopInterestApiArgSchema,
        responseSchema: WorkshopInterestResponseBodySchema,
      },
    }),

    changeWorkshopInterestBranch: build.mutation<void, ChangeWorkshopInterestBranchApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest/${queryArg.id}/change-branch`,
        method: 'POST',
        body: queryArg.changeWorkshopInterestBranchRequestBody,
        queryArg,
      }),
      invalidatesTags: (result, error, queryArg) => [{type: 'WorkshopInterest', id: queryArg.id}],
      extraOptions: {
        requestSchema: ChangeWorkshopInterestBranchRequestBodySchema,
      },
    }),

    changeWorkshopInterestContactPerson: build.mutation<
      void,
      ChangeWorkshopInterestContactPersonApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest/${queryArg.id}/change-contact-person`,
        method: 'POST',
        body: queryArg.changeWorkshopInterestContactPersonRequestBody,
        queryArg,
      }),
      invalidatesTags: (result, error, queryArg) => [
        {type: 'WorkshopInterest', id: queryArg.id},
        'Customer',
      ],
      extraOptions: {
        requestSchema: ChangeWorkshopInterestContactPersonRequestBodySchema,
      },
    }),

    setWorkshopInterestSource: build.mutation<void, SetWorkshopInterestSourceApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest/${queryArg.id}/source`,
        method: 'PUT',
        body: queryArg.setWorkshopInterestSourceRequestBody,
        queryArg,
      }),
      invalidatesTags: (result, error, queryArg) => [{type: 'WorkshopInterest', id: queryArg.id}],
      extraOptions: {
        requestSchema: SetWorkshopInterestSourceRequestBodySchema,
      },
    }),

    listWorkshopInterestActivities: build.query<
      ListWorkshopInterestActivitiesApiResponse,
      ListWorkshopInterestActivitiesApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest/${queryArg.interestId}/activity`,
        queryArg,
      }),
      providesTags: (result, error, queryArg) => [
        {type: 'WorkshopInterest', id: queryArg.interestId},
      ],
      extraOptions: {
        requestSchema: ListWorkshopInterestActivitiesApiArgSchema,
        responseSchema: WorkshopInterestActivityResponseBodySchema.array(),
      },
    }),

    createWorkshopInterestActivity: build.mutation<
      CreateWorkshopInterestActivityApiResponse,
      CreateWorkshopInterestActivityApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest/${queryArg.interestId}/activity`,
        method: 'POST',
        body: queryArg.createWorkshopInterestActivityRequestBody,
        queryArg,
      }),
      invalidatesTags: (_, __, arg) => [{type: 'WorkshopInterest', id: arg.interestId}],
      extraOptions: {
        requestSchema: CreateWorkshopInterestActivityRequestBodySchema,
        responseSchema: UuidResponseBodySchema,
      },
    }),

    deleteWorkshopInterest: build.mutation<void, DeleteWorkshopInterestApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest/${queryArg.id}`,
        method: 'DELETE',
        queryArg,
      }),
      invalidatesTags: ['WorkshopInterest'],
    }),

    openWorkshopInterest: build.mutation<void, OpenWorkshopInterestApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest/${queryArg.id}/open`,
        method: 'POST',
        queryArg,
      }),
      invalidatesTags: (result, error, queryArg) => [{type: 'WorkshopInterest', id: queryArg.id}],
    }),

    endWorkshopInterest: build.mutation<void, EndWorkshopInterestApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest/${queryArg.id}/end`,
        method: 'POST',
        body: queryArg.endWorkshopInterestRequestBody,
        queryArg,
      }),
      invalidatesTags: (result, error, queryArg) => [{type: 'WorkshopInterest', id: queryArg.id}],
      extraOptions: {
        requestSchema: EndWorkshopInterestRequestBodySchema,
      },
    }),

    selectWorkshopInterestVehicle: build.mutation<void, SelectVehicleApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest/${queryArg.id}/select-vehicle`,
        method: 'POST',
        body: queryArg.selectVehicleRequestBody,
        queryArg,
      }),
      invalidatesTags: (result, error, queryArg) => [{type: 'WorkshopInterest', id: queryArg.id}],
      extraOptions: {
        requestSchema: SelectVehicleRequestBodySchema,
      },
    }),

    removeWorkshopInterestVehicle: build.mutation<void, RemoveVehicleApiArg>({
      query: (queryArg) => ({
        url: `/dms/v1/workshop/interest/${queryArg.id}/remove-vehicle`,
        method: 'POST',
        queryArg,
      }),
      invalidatesTags: (result, error, queryArg) => [{type: 'WorkshopInterest', id: queryArg.id}],
    }),
  }),
});

export const {
  useCreateWorkshopInterestMutation,
  useGetWorkshopInterestQuery,
  useChangeWorkshopInterestBranchMutation,
  useChangeWorkshopInterestContactPersonMutation,
  useSetWorkshopInterestSourceMutation,
  useListWorkshopInterestActivitiesQuery,
  useCreateWorkshopInterestActivityMutation,
  useDeleteWorkshopInterestMutation,
  useOpenWorkshopInterestMutation,
  useEndWorkshopInterestMutation,
  useSelectWorkshopInterestVehicleMutation,
  useRemoveWorkshopInterestVehicleMutation,
} = injected;
